import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Users,
  FileCheck,
  Repeat,
  Siren,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Mail,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const services = [
  {
    Icon: Users,
    title: "OT Organisation Assessment",
    text: "Standortbestimmung Ihrer OT-Organisation: Rollen, Verantwortlichkeiten, Schnittstellen zwischen IT, OT, Facility und Geschäftsleitung, Reifegrad nach IEC 62443-2-1 und NIS-2.",
  },
  {
    Icon: FileCheck,
    title: "OT Governance & Security Policies",
    text: "Aufbau tragfähiger OT-Security-Richtlinien, Rollenkonzepte und RACI für Gebäudeautomation: Wer darf was, wer entscheidet, wer informiert wen bei Incidents und Änderungen?",
  },
  {
    Icon: Repeat,
    title: "OT Betriebsmodell",
    text: "Einführung oder Optimierung der OT-Prozesse für Change, Patch, Incident, Backup und Wartung, verzahnt mit ITIL, Facility-Management und Integratoren, ohne den laufenden Betrieb zu gefährden.",
  },
  {
    Icon: Siren,
    title: "Incident Response & Meldewesen",
    text: "Klare Eskalations- und Meldewege für OT-Sicherheitsvorfälle in Gebäudeautomation, abgestimmt auf NIS-2 Meldepflichten, IEC 62443 und die Zusammenarbeit mit IT-Security und Betrieb.",
  },
];

const benefits = [
  "Sie schaffen klare Verantwortlichkeiten für OT-Security zwischen IT, OT, Facility und Geschäftsleitung.",
  "Sie etablieren Prozesse, die im Alltag funktionieren, statt nur auf dem Papier.",
  "Sie erfüllen organisatorische Anforderungen aus NIS-2, IEC 62443-2-1 und BSI INF.13 / INF.14 nachweisbar.",
  "Sie verkürzen Reaktionszeiten bei Vorfällen durch klare Eskalations- und Meldewege.",
  "Sie schaffen eine gemeinsame Sprache zwischen Technik, Betrieb und Führungsebene.",
];

const faqs = [
  {
    q: "Was ist eine OT-Organisation?",
    a: "Eine OT-Organisation beschreibt Rollen, Verantwortlichkeiten, Prozesse und Schnittstellen für den sicheren Betrieb von Operational Technology, also industriellen Steuerungs- und Gebäudeautomationssystemen. Sie ergänzt die technische Sicherheit durch klare Governance und tragfähige Abläufe.",
  },
  {
    q: "Warum reicht Technik allein nicht aus?",
    a: "Firewalls, Zertifikate und gepatchte Systeme nützen wenig, wenn niemand klar für Wartung, Änderungen und Incidents zuständig ist. OT-Security braucht eine Organisation, die im Alltag Entscheidungen trifft, Eskaliert und dokumentiert.",
  },
  {
    q: "Was sagt IEC 62443-2-1 zur OT-Organisation?",
    a: "IEC 62443-2-1 beschreibt ein Security-Management-System für Betreiber von IACS. Sie fordert unter anderem Richtlinien, Rollen, Risikomanagement, Schulung, Incident Management und Audits. Für Gebäudeautomation lässt sich dieses Betreibermodell direkt übertragen.",
  },
  {
    q: "Was hat OT-Organisation mit NIS-2 zu tun?",
    a: "NIS-2 verlangt Governance, Risikomanagement, Meldepflichten und Lieferkettensicherheit. All das lässt sich nur mit einer klaren OT-Organisation umsetzen und nachweisen. Wer die Verantwortlichkeiten nicht definiert, kann die Pflichten nicht erfüllen.",
  },
  {
    q: "Wer sollte in die OT-Organisation einbezogen werden?",
    a: "Typische Rollen sind Facility Management, Gebäudeautomationstechniker, IT-Security, Netzwerk, Integratoren, Lieferanten und die Geschäftsleitung. Je nach Liegenschaft kommen auch Sicherheitsbeauftragte, Datenschutz und Compliance hinzu.",
  },
  {
    q: "Wie starten wir konkret?",
    a: "Der pragmatische Einstieg ist ein OT Organisation Assessment, das Rollen, Prozesse und Reifegrad bewertet. Alternativ starten Sie mit dem FACHSPRECHPARTNER Workbook inklusive Self-Assessment und 60 Minuten Online Assessment, um daraus einen priorisierten Fahrplan abzuleiten.",
  },
];

const OtOrganisation = () => {
  const openContact = () =>
    window.dispatchEvent(new CustomEvent("open-contact-form"));

  const canonical = "https://fachsprechpartner.de/ot-organisation";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: "OT-Organisation für Gebäudeautomation",
        serviceType: "OT-Security Organisation und Governance",
        provider: {
          "@type": "Organization",
          name: "FACHSPRECHPARTNER",
          url: "https://fachsprechpartner.de",
        },
        areaServed: { "@type": "Country", name: "Deutschland" },
        description:
          "OT-Organisation für Gebäudeautomation: Assessment, Governance & Policies, Betriebsmodell und Incident Response nach IEC 62443-2-1 und NIS-2.",
        url: canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fachsprechpartner.de/" },
          { "@type": "ListItem", position: 2, name: "OT-Organisation", item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>OT-Organisation für Gebäudeautomation & OT-Security</title>
        <meta
          name="description"
          content="OT-Organisation für Gebäudeautomation: Rollen, Verantwortlichkeiten, Governance und Betriebsmodell nach IEC 62443-2-1 und NIS-2. Assessment und Workbook."
        />
        <meta
          name="keywords"
          content="OT Organisation, OT Governance, OT Betriebsmodell, IEC 62443-2-1, OT Security Organisation, Gebäudeautomation Organisation, NIS-2 Governance, OT Rollen, OT Prozesse"
        />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="de" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={canonical} />
        <meta property="og:title" content="OT-Organisation für Gebäudeautomation & OT-Security" />
        <meta
          property="og:description"
          content="OT-Organisation für Gebäudeautomation: Rollen, Verantwortlichkeiten, Governance und Betriebsmodell nach IEC 62443-2-1 und NIS-2."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta property="og:image:type" content="image/webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FACHSPRECHPARTNER Tim Bauer, OT-Security und Gebäudeautomation" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content="Fachsprechpartner" />
        <meta name="twitter:title" content="OT-Organisation für Gebäudeautomation & OT-Security" />
        <meta name="twitter:description" content="OT-Organisation für Gebäudeautomation: Rollen, Verantwortlichkeiten, Governance und Betriebsmodell nach IEC 62443-2-1 und NIS-2." />
        <meta name="twitter:image" content="https://fachsprechpartner.de/og-image.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-primary to-primary/90 text-primary-foreground">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-semibold uppercase tracking-wider mb-6">
                <ShieldCheck className="w-4 h-4" />
                OT-Organisation · Governance
              </div>
              <h1 className="text-4xl md:text-6xl font-heading font-bold leading-tight mb-6">
                OT-Organisation: Klare Verantwortung für sicheren Betrieb.
              </h1>
              <p className="text-lg md:text-xl text-primary-foreground/85 mb-8 leading-relaxed max-w-3xl">
                Ich helfe Ihnen, Ihre OT-Organisation für Gebäudeautomation
                aufzubauen: von Rollen und Verantwortlichkeiten über Governance
                und Betriebsmodell bis hin zu Incident Response und Meldewesen,
                abgestimmt auf IEC 62443-2-1 und NIS-2.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openContact}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
                >
                  Assessment anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/workbook"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 text-primary-foreground font-semibold hover:bg-primary-foreground/10 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Zum Workbook
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="py-16 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Leistungen rund um OT-Organisation
            </h2>
            <p className="text-muted-foreground mb-12 max-w-2xl">
              Vier Bausteine, die aus technischer Sicherheit eine lebendige,
              nachweisbare OT-Organisation machen, die im Betrieb funktioniert.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map(({ Icon, title, text }) => (
                <div
                  key={title}
                  className="p-6 rounded-2xl border border-border bg-card hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-2">
                    {title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Nutzen */}
        <section className="py-16 md:py-24 bg-muted/40">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Was Sie davon haben
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                OT-Security lebt von klaren Rollen und Prozessen. Eine gute
                OT-Organisation macht technische Maßnahmen nachhaltig wirksam
                und prüfbar.
              </p>
            </div>
            <ul className="space-y-3">
              {benefits.map((b) => (
                <li key={b} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <span className="text-foreground/90">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-8">
              Häufige Fragen zur OT-Organisation
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ihre OT-Organisation aufbauen
            </h2>
            <p className="text-primary-foreground/85 mb-8 leading-relaxed">
              Starten Sie mit einem Assessment oder dem Workbook. In beiden
              Fällen bekommen Sie klare Rollen, Prozesse und einen priorisierten
              Fahrplan für nachweisbare OT-Security.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/workbook"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Workbook ansehen
              </Link>
              <button
                onClick={openContact}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary-foreground/30 font-semibold hover:bg-primary-foreground/10 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Sparring anfragen
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default OtOrganisation;
